'use strict';

import Utils from 'components/utils';
import { hasConsent, getAnalyticsConsentGroup, onConsentChanged } from './onetrust.js';

/**
 * Applies Matomo consent for the configured OneTrust group.
 * Uses the live SDK state when available; falls back to the OptanonConsent cookie.
 */
function applyMatomoConsent() {
    const matomoGroup = getAnalyticsConsentGroup();
    const consented = !!(matomoGroup && hasConsent(matomoGroup));
    if (consented) {
        window._paq.push(['setConsentGiven']);
        window._paq.push(['rememberConsentGiven']);
    } else {
        window._paq.push(['forgetConsentGiven']);
    }
}

class Matomo {
    constructor() {
        window._paq = window._paq || [];

        if (document.documentElement.dataset.onetrustConsentIntegrationEnabled === 'true') {
            window._paq.push(['requireConsent']);
            applyMatomoConsent();
            onConsentChanged(() => applyMatomoConsent());
        }

        const metaData = document.getElementById("global-meta-data");

        const URLparams = new URLSearchParams(window.location.search);
        this.prevPage = URLparams.get('page');

        /**
         * BUGFIX TOGGLE
         * CYT-19621 [A11Y-116] Ensure custom controls provide proper textual name, role, and state information
         */
        const matomov5MsteamsFixEnabled = document.documentElement.dataset.matomov5ErrorMsteamsFix;

        let matomo = metaData.textContent.replace(/\\x([0-9a-f]{2})/g, (_, pair) => String.fromCharCode(parseInt(pair, 16)));
        let matomoJsonObject = JSON.parse(matomo);

        this.matomoVersion = this.getMatomoVersion();

        for (const key of Object.keys(matomoJsonObject.matomo.set)) {
            const value = matomoJsonObject.matomo.set[key];

            if (key === "customVariable") {
                const visit = matomoJsonObject.matomo.set.customVariable.visit;
                for (let visitIndex =0 ; visitIndex < visit.length ; visitIndex++ ) {
                    if (this.matomoVersion == '3') {
                        window._paq.push(['setCustomVariable',
                            visit[visitIndex].index, visit[visitIndex].name.replace(/"/g, "'"), visit[visitIndex].value.replace(/"/g, "'") , 'visit']);
                    } else if (this.matomoVersion == "5") {
                        window._paq.push(['setCustomDimension',
                            visit[visitIndex].indexForMatomo5, visit[visitIndex].value.replace(/"/g, "'")]);
                    }
                }
                const page = matomoJsonObject.matomo.set.customVariable.page;
                for (let pageIndex =0 ; pageIndex < page.length ; pageIndex++ ) {
                    if (this.matomoVersion == "3") {
                        window._paq.push(['setCustomVariable',
                            page[pageIndex].index, page[pageIndex].name.replace(/"/g, "'"), page[pageIndex].value.replace(/"/g, "'") , 'page']);
                    } else if (this.matomoVersion == "5") {
                        window._paq.push(['setCustomDimension',
                            page[pageIndex].indexForMatomo5, page[pageIndex].value.replace(/"/g, "'")]);
                    }
                }
            } else if (key === "customUrl" && this.prevPage) {
                    window._paq.push([('set' + key.charAt(0).toUpperCase() + key.substring(1)).replace(/"/g, "'"), value.replace(/"/g, "'") + '-' + this.prevPage]);
            } else {
                window._paq.push([('set' + key.charAt(0).toUpperCase() + key.substring(1)).replace(/"/g, "'"), value.replace(/"/g, "'")]);
            }
        }

        if (this.matomoVersion == "5") {
            window._paq.push(['disableAlwaysUseSendBeacon']);
            if (matomov5MsteamsFixEnabled) {
                window._paq.push(['setCookieSameSite', 'None']);
            }
            window._paq.push(['setSecureCookie', true]);
        }

        this.matomoScriptFile = this.getMatomoScriptFile();
        this.sessionId = this.getSessionId();


        // Largest contentful paint metric is available only for chrome browser
        this.lcp = 0;

    }

    init() {
        // should be integrated for SSO

        if (Utils.isChrome()) {
            this.createPerformanceObserver();
        }

        this.checkMatomoSession();
        this.setCustomVariablesPage();
        this.createMatomoScriptTag();
    }


    createPerformanceObserver() {
        // Create the PerformanceObserver instance.
        let po = new PerformanceObserver((entryList) => {
            let entries = entryList.getEntries(),
                lastEntry = entries ? entries[entries.length - 1] : null;

            // Update `lcp` to the latest value, using `renderTime` if it's available,
            // otherwise using `loadTime`. (Note: `renderTime` may not be available if
            // the element is an image and it's loaded cross-origin without the
            // `Timing-Allow-Origin` header)
            this.lcp = lastEntry ? Math.round(lastEntry.renderTime) || Math.round(lastEntry.loadTime) : null;
        });

        // Observe entries of type `largest-contentful-paint`, including buffered
        // entries, i.e. entries that occurred before calling `observe()`.
        po.observe({type: 'largest-contentful-paint', buffered: true});
    }


    checkMatomoSession() {
        if (window.localStorage) {
            var previousSession = window.localStorage.getItem("previousSession");
            if (!previousSession || previousSession != this.sessionId) {
                _paq.push(['appendToTrackingUrl', 'new_visit=1']);
                window.localStorage.setItem("previousSession", this.sessionId);
            }
        }
    }

    setCustomVariablesPage() {
        let _this = this;
        // calcul performance time when page loaded, synchronous with timeout 0 to have all data from window.performance object
        window.addEventListener("load", function (event) {
            setTimeout(function () {
                const matomoVersion = document.documentElement.dataset.matomoVersion;
                // window._paq = window._paq || [];
                let perf = {
                    net: Math.round(window.performance.timing.requestStart - window.performance.timing.navigationStart),
                    cyt: Math.round(window.performance.timing.responseStart - window.performance.timing.requestStart),
                    res: Math.round(window.performance.timing.responseEnd - window.performance.timing.responseStart),
                    dom: Math.round(window.performance.timing.domComplete - window.performance.timing.domLoading),
                    lod: Math.round(window.performance.timing.loadEventEnd - window.performance.timing.loadEventStart),
                    lcp: _this.lcp
                }

                if (matomoVersion == "3") {
                    const navigationTime = Math.round(window.performance.timing.loadEventEnd - window.performance.timing.navigationStart);
                    window._paq.push(['setCustomVariable', 1, 'perf', JSON.stringify(perf), 'page']);
                    window._paq.push(['setGenerationTimeMs', navigationTime]);
                } else if (matomoVersion == "5") {
                    window._paq.push(['setCustomDimension', 8, JSON.stringify(perf)]);
                }

                window._paq.push(['trackPageView']);
            }, 1);
        });
    }

    createMatomoScriptTag() {
        let d = document,
            g = d.createElement('script'),
            matomoVersionSrc;

        const s = d.getElementsByTagName('script')[0];

        if (this.matomoVersion == "3") {
            matomoVersionSrc = "/a/b/m3.js";
        } else if (this.matomoVersion == "5") {
            matomoVersionSrc = "/a/b/m5.js";
        }

        g.type = 'text/javascript';
        g.async = true;
        g.defer = true;
        g.src = this.matomoScriptFile ? this.matomoScriptFile : matomoVersionSrc;
        s.parentNode.insertBefore(g, s);
    }

    getMatomoVersion() {
        return document.documentElement.dataset.matomoVersion;
    }

    getMatomoScriptFile() {
        return document.documentElement.dataset.matomoScriptFile;
    }

    getSessionId() {
        if (document.documentElement.dataset.sessionId) {
            return document.documentElement.dataset.sessionId.split('~')[0];
        } else {
            return "";
        }
    }

    trackEvent(eventName, eventType, eventDesc, data) {
        if (this.matomoVersion == "3") {
            for (let i = 1; i < 6; i++) {
                window._paq.push(['deleteCustomVariable', i, "page"]);
            }
        } else if (this.matomoVersion == "5") {
            for (let i = 6; i < 11; i++) {
                window._paq.push(['deleteCustomDimension', i]);
            }
        }

        if (data != null) {
            if (this.matomoVersion == "3") {
                window._paq.push(['setCustomVariable', 1, 'data',JSON.stringify(data), 'page']);
            } else if (this.matomoVersion == "5") {
                window._paq.push(['setCustomDimension', 9, JSON.stringify(data)]);
            }
        }

        window._paq.push(['trackEvent', eventName, eventType, eventDesc]);
    }
}

export {Matomo as default};